import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Gift, Users, Sparkles, Copy } from "lucide-react";
import Seo from "@/components/common/Seo";
import { Reveal } from "@/components/common/Reveal";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function Refer() {
  const { user } = useAuth();
  const [ref, setRef] = useState(null);

  useEffect(() => {
    if (user) api.get("/me/dashboard").then(({ data }) => setRef(data.referral)).catch(() => {});
  }, [user]);

  const steps = [
    { icon: Users, title: "Share your link", body: "Send your unique referral link to friends and neighbours." },
    { icon: Sparkles, title: "They book & save", body: "Your friend gets a $20 credit on their first detail." },
    { icon: Gift, title: "You both get $20", body: "When they complete their first booking, you each earn $20 — automatically." },
  ];

  const copy = () => { navigator.clipboard.writeText(ref.link); toast.success("Referral link copied!"); };

  return (
    <div data-testid="refer-page">
      <Seo title="Refer a Friend — Give $20, Get $20 | Reverence Autos" description="Refer friends to Reverence Autos mobile detailing in Ottawa. When they complete their first booking, you both get a $20 credit." path="/refer" />
      <section className="relative overflow-hidden bg-brand-deep-red text-white grain">
        <div className="container-rv relative z-10 py-24 text-center lg:py-32">
          <Reveal>
            <p className="overline text-white/70">Refer a Friend</p>
            <h1 className="mx-auto mt-4 max-w-3xl font-serif text-5xl font-bold leading-tight sm:text-6xl">Give $20, Get $20</h1>
            <p className="mx-auto mt-5 max-w-xl text-white/80">Love how your car feels? Share the shine. When your friend completes their first booking, a $20 credit lands on both accounts.</p>
            {user && ref ? (
              <div className="mx-auto mt-9 max-w-md rounded-2xl bg-white/10 p-6 backdrop-blur-sm">
                <p className="text-sm text-white/70">Your referral link</p>
                <button onClick={copy} data-testid="refer-copy-link" className="mt-2 flex w-full items-center justify-between gap-3 rounded-full bg-white px-5 py-3 text-sm font-medium text-brand-charcoal">
                  <span className="truncate">{ref.link}</span><Copy className="h-4 w-4 shrink-0 text-brand-red" />
                </button>
                <p className="mt-3 text-sm text-white/80">{ref.count} referred · ${ref.credits.toFixed(0)} earned</p>
              </div>
            ) : (
              <Link to="/login" className="mt-9 inline-flex rounded-full bg-white px-7 py-3.5 text-sm font-semibold text-brand-red transition-transform hover:-translate-y-0.5">Log in to get your link</Link>
            )}
          </Reveal>
        </div>
      </section>
      <section className="bg-white">
        <div className="container-rv py-20 lg:py-28">
          <div className="grid gap-7 md:grid-cols-3">
            {steps.map((s, i) => (
              <Reveal key={i} delay={i * 0.1}>
                <div className="card-rv h-full p-8">
                  <div className="grid h-12 w-12 place-items-center rounded-full bg-brand-red/10 text-brand-red"><s.icon className="h-6 w-6" /></div>
                  <h3 className="mt-5 font-serif text-2xl font-bold text-brand-charcoal">{s.title}</h3>
                  <p className="mt-3 text-brand-muted">{s.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
